import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from './components/AppLayout';
import { CookieBanner } from './components/CookieBanner';
import { PublicLayout } from './components/PublicLayout';
import { Spinner } from './components/ui';
import { AccountPage } from './pages/AccountPage';
import { BillingPage } from './pages/BillingPage';
import { CallbackPage } from './pages/CallbackPage';
import { LinkPage } from './pages/LinkPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { DiscoveryPage } from './pages/DiscoveryPage';
import { ForgotPage } from './pages/ForgotPage';
import { InvitePage } from './pages/InvitePage';
import { InvoicesPage } from './pages/InvoicesPage';
import { LoginPage } from './pages/LoginPage';
import { OrganizationPage } from './pages/OrganizationPage';
import { ResetPage } from './pages/ResetPage';
import { SignupPage } from './pages/SignupPage';
import { UsagePage } from './pages/UsagePage';
import { VerifyPage } from './pages/VerifyPage';
import { activeHandoff, handoffFromSearch } from './services/handoff';
import { useSession } from './session';

/** Signed-in pages: a missing session goes to /login and comes back here afterwards, and so does a first connection
 *  through the question it is asked (pages/DiscoveryPage.tsx). */
export function RequireSession() {
  const { state } = useSession();
  const location = useLocation();
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'anonymous') return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  if (state.user.discovery_asked === false && location.pathname !== '/discovery') return <Navigate to="/discovery" replace state={{ returnTo: location.pathname + location.search + location.hash }} />;
  return <Outlet />;
}

/** Public auth pages: a signed-in user has no business on /login or /signup — except inside the add-in's dialog
 *  (services/handoff.ts), where a session left in the dialog's storage must not skip the sign-in. */
export function AnonymousOnly() {
  const { state } = useSession();
  const location = useLocation();
  if (handoffFromSearch(location.search) || activeHandoff()) return <Outlet />;
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'signed-in') return <Navigate to={(location.state as { returnTo?: string } | null)?.returnTo ?? '/'} replace />;
  return <Outlet />;
}

function Loading() {
  const { t } = useTranslation();
  return (
    <PublicLayout title={t('app.loading')}>
      <Spinner />
    </PublicLayout>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <PublicLayout title={t('app.notFound')}>
      <a href="/" className="text-center text-[13px] text-accent underline">
        {t('app.goHome')}
      </a>
    </PublicLayout>
  );
}

export default function App() {
  return (
    <>
      <AppRoutes />
      <CookieBanner />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<AnonymousOnly />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
      </Route>
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/forgot" element={<ForgotPage />} />
      <Route path="/reset" element={<ResetPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/auth/callback" element={<CallbackPage />} />
      {/* The add-in opens the portal signed in (services/link.ts). */}
      <Route path="/auth/link" element={<LinkPage />} />
      {/* The billing mails link here (sculpt/billing/subscriptions.py). */}
      <Route path="/organization/billing" element={<Navigate to="/billing" replace />} />
      <Route element={<RequireSession />}>
        <Route path="/discovery" element={<DiscoveryPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route element={<AppLayout />}>
          <Route index element={<AccountPage />} />
          <Route path="/organization" element={<OrganizationPage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
